import { AppForm } from '../../../../../../common/forms/app.form';
import { FormControl, Validators } from '@angular/forms';
import { CustomValidator } from '../../../../../../common/forms/custom.validators';
import { ApplicantAppEntityApplicantWrite } from '../../../../../../openapi';
import { Injectable } from '@angular/core';
import { TokenData } from '../../../../../../services/ssession.service';
import { UserDetails } from '../../../../../../store/reducers/user-details.reducer';
import { ApplicantCorrespondenceForm } from './applicant-correspondence.form';

@Injectable()
export class ApplicantForm extends AppForm {
  constructor() {
    super({
      applicantType: new FormControl('', {
        validators: [Validators.required],
        nonNullable: true
      }),
      name: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      firstName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      lastName: new FormControl('', {
        nonNullable: true,
        validators: [Validators.required, Validators.maxLength(255)]
      }),
      pesel: new FormControl('', { nonNullable: true, validators: [CustomValidator.pesel()] }),
      nip: new FormControl('', { nonNullable: true, validators: [Validators.required, CustomValidator.nip()] }),
      phone: new FormControl('', {
        validators: [Validators.required, CustomValidator.phone(), Validators.maxLength(255)],
        nonNullable: true
      }),
      phone2: new FormControl('', {
        validators: [CustomValidator.phone(), Validators.maxLength(255)],
        nonNullable: true
      }),
      email: new FormControl('', {
        validators: [
          Validators.required,
          CustomValidator.email(),
          Validators.maxLength(255)
        ],
        nonNullable: true
      }),
      country: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      voivodeship: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      county: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      municipality: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      zipCode: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      postOffice: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      city: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      street: new FormControl('', {
        validators: [Validators.maxLength(255)],
        nonNullable: true
      }),
      buildingNumber: new FormControl('', {
        validators: [Validators.required, Validators.maxLength(255)],
        nonNullable: true
      }),
      apartmentNumber: new FormControl('', Validators.maxLength(255)),
      differentCorrespondenceAddress: new FormControl(
        false,
        { nonNullable: true, validators: [Validators.required] }
      )
    });
  }

  public get differentCorrespondenceAddress(): FormControl {
    return this.get('differentCorrespondenceAddress') as FormControl;
  }

  public differentCorrespondenceAddressChange(v: boolean): void {
    if (v) {
      this.addControl('correspondenceForm', new ApplicantCorrespondenceForm());
    } else {
      this.removeControl('correspondenceForm');
    }
  }

  public get correspondenceForm(): ApplicantCorrespondenceForm | null {
    return this.get('correspondenceForm') as ApplicantCorrespondenceForm | null;
  }

  public get country(): FormControl {
    return this.get('country') as FormControl;
  }

  public get voivodeship(): FormControl {
    return this.get('voivodeship') as FormControl;
  }

  public get county(): FormControl {
    return this.get('county') as FormControl;
  }

  public get municipality(): FormControl {
    return this.get('municipality') as FormControl;
  }

  public get nip(): FormControl {
    return this.get('nip') as FormControl;
  }

  public get pesel(): FormControl | null {
    return this.get('pesel') as FormControl | null;
  }

  public get name(): FormControl {
    return this.get('name') as FormControl;
  }

  public get lastName(): FormControl {
    return this.get('lastName') as FormControl;
  }

  public get firstName(): FormControl {
    return this.get('firstName') as FormControl;
  }

  public get applicantType(): FormControl {
    return this.get('applicantType') as FormControl;
  }

  public override patchFormValues(v: ApplicantAppEntityApplicantWrite): void {
    this.differentCorrespondenceAddressChange(!!v?.differentCorrespondenceAddress);
    super.patchFormValues({
      ...v,
      zipCode: v?.postalCode,
      buildingNumber: v?.houseNumber,
      phone: v?.phoneNumber,
      phone2: v?.phoneNumber2
    });
    this.correspondenceForm?.patchValue(v);
  }

  public toWriteModel(): ApplicantAppEntityApplicantWrite {
    return {
      entityType: this.value.applicantType,
      name: this.value.name,
      pesel: this.value?.pesel || null,
      nip: this.value.nip || null,
      firstName: this.value.firstName,
      lastName: this.value.lastName,
      phoneNumber: this.value.phone,
      email: this.value.email,
      city: this.value.city,
      houseNumber: this.value.buildingNumber,
      postalCode: this.value.zipCode,
      postOffice: this.value.postOffice,
      apartmentNumber: this.value.apartmentNumber,
      phoneNumber2: this.value.phone2,
      street: this.value.street,
      country: this.value?.country || null,
      voivodeship: this.value?.voivodeship || null,
      county: this.value?.county || null,
      municipality: this.value?.municipality || null,

      differentCorrespondenceAddress: this.differentCorrespondenceAddress.value,
      ...this.correspondenceForm?.toWriteModel()
    };
  }

  public readApplicantDataFromPortal(v: UserDetails | null): void {
    this.patchValue({
      firstName: v?.firstName || '',
      lastName: v?.lastName || '',
      email: v?.email || '',
      phone: v?.phoneNumber || '',
      city: v?.city,
      buildingNumber: v?.houseNumber,
      apartmentNumber: v?.apartmentNumber || '',
      zipCode: v?.postalCode || '',
      postOffice: v?.postOffice || '',
      street: v?.street
    });
  }

  public readApplicantDataFromGovPl(v: TokenData | null): void {
    this.patchValue({
      firstName: v?.firstName || '',
      lastName: v?.lastName || '',
      email: v?.email || '',
      city: '',
      buildingNumber: '',
      apartmentNumber: '',
      zipCode: '',
      postOffice: '',
      street: '',
      pesel: v?.pesel
    });
  }
}
